import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  CircularProgress,
  FormHelperText,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Autocomplete,
} from '@mui/material';
import { InlineDatePicker } from '../../components/InlineDatePicker';
import {
  ActionDetails,
  PriorityEnum,
  ActionStatusEnum,
  RaidCategoryEnum,
  RaidItemCreate,
  User,
  getUsers
} from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import { getDefaultExpectedDate } from './raidFormUtils';

interface ActionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RaidItemCreate) => void;
  initialData?: ActionDetails | null;
  itemId?: number | null;
  title?: string;
  readOnly?: boolean; // Add read-only mode for when review is submitted
  isFromOtherQuestionnaire?: boolean; // Indicates if item is from another questionnaire
  defaultAssigneeId?: string | number | null;
  initialPartialRaid?: boolean;
}

const ActionDialog: React.FC<ActionDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  itemId,
  title = '',
  readOnly = false, // Default to false for backward compatibility
  isFromOtherQuestionnaire = false,
  defaultAssigneeId,
  initialPartialRaid = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [formData, setFormData] = useState<ActionDetails>({
    description: '',
    priority: PriorityEnum.MEDIUM,
    person_responsible: '',
    status: ActionStatusEnum.OPEN,
  });
  const [formTitle, setFormTitle] = useState('');
  const [plannedCompletionDate, setPlannedCompletionDate] = useState('');
  const [actualCompletionDate, setActualCompletionDate] = useState('');
  const [partialRaid, setPartialRaid] = useState(false);
  // Partial RAID applies only on initial create, never when editing an existing item
  const isCreating = !itemId;
  const effectivePartialRaid = isCreating && partialRaid;
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when initialData changes
  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getUsers();
        // Filter out System User and inactive users
        const filteredUsers = response.data.filter((user: User) => 
          user.display_name !== 'System User' && 
          user.user_name !== 'system_user' &&
          user.status === 'ACTIVE'
        );
        setUsers(filteredUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    if (open) {
      fetchUsers();
    }
  }, [open]);

  // Helper function to find user by ID
  const findUserById = (userId: string): User | null => {
    if (!userId) return null;
    return users.find(u => u.id.toString() === userId) || null;
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        status: initialData.status || ActionStatusEnum.OPEN,
      });
      
      // Set dates from initialData if they exist
      if (initialData.planned_competion_date) {
        setPlannedCompletionDate(initialData.planned_competion_date);
      }
      if (initialData.actual_completion_date) {
        setActualCompletionDate(initialData.actual_completion_date);
      }
    } else {
      // Reset form for new entries
      setFormData({
        description: '',
        priority: PriorityEnum.MEDIUM,
        person_responsible: '',
        status: ActionStatusEnum.OPEN,
      });
      
      // Set current date as default for new items
      const currentDate = getCurrentDate();
      setPlannedCompletionDate(currentDate);
      setActualCompletionDate(currentDate);
    }
    
    // Set the title
    setFormTitle(title || '');
    setPartialRaid(itemId ? false : !!initialPartialRaid);
  }, [initialData, title, initialPartialRaid, itemId]);

  const applyPartialRaidDefaults = () => {
    if (initialData) return;
    if (!formData.person_responsible && defaultAssigneeId != null && defaultAssigneeId !== '') {
      setFormData(prev => ({
        ...prev,
        person_responsible: String(defaultAssigneeId),
      }));
    }
    if (!plannedCompletionDate) {
      setPlannedCompletionDate(getDefaultExpectedDate());
    }
  };

  const handleTextChange = (field: keyof ActionDetails) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSelectChange = (field: keyof ActionDetails) => (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormTitle(event.target.value);
    
    // Clear error when title is updated
    if (errors.title) {
      setErrors(prev => ({
        ...prev,
        title: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formTitle.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.person_responsible?.trim()) {
      newErrors.person_responsible = 'Responsible person is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Prepare the data with dates
      const submitData: RaidItemCreate = {
        entity_id: 0, // Will be set by parent component
        title: formTitle,
        raid_category: RaidCategoryEnum.ACTION,
        partial_raid: effectivePartialRaid,
        details: {
          ...formData,
          planned_competion_date: plannedCompletionDate || undefined,
          actual_completion_date: actualCompletionDate || undefined,
        }
      };
      
      onSubmit(submitData);
      setLoading(false);
      onClose();
    } catch (error) {
      console.error('Error submitting action item:', error);
      setLoading(false);
      setErrors(prev => ({
        ...prev,
        submit: 'Failed to submit action item. Please try again.'
      }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {readOnly ? 'View Action' : (itemId ? 'Update Action' : 'Add Action')}
        {loading && <CircularProgress size={20} sx={{ ml: 2 }} />}
      </DialogTitle>
      
      <DialogContent>
        {readOnly && (
          <Typography color="info.main" variant="subtitle2" sx={{ mb: 2 }}>
            {isFromOtherQuestionnaire 
              ? 'This item is from other reviews and is displayed in read-only mode.'
              : 'This review is submitted. RAID details are in read-only mode.'
            }
          </Typography>
        )}
        {!readOnly && isCreating && (
          <FormControlLabel
            control={
              <Switch
                checked={partialRaid}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setPartialRaid(enabled);
                  if (enabled) {
                    applyPartialRaidDefaults();
                  }
                }}
              />
            }
            label="Partial RAID (show only required fields)"
            sx={{ mb: 1 }}
          />
        )}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Title"
                value={formTitle}
                onChange={handleTitleChange}
                fullWidth
                required
                error={!!errors.title}
                helperText={errors.title || 'Brief, descriptive title for the action'}
                InputProps={{ readOnly }}
                disabled={readOnly}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={handleTextChange('description')}
                multiline
                rows={4}
                fullWidth
                required
                error={!!errors.description}
                helperText={errors.description || 'Detailed description of the action'}
                InputProps={{ readOnly }}
                disabled={readOnly}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={handleSelectChange('priority') as any}
                  readOnly={readOnly}
                  inputProps={{ readOnly }}
                >
                  {Object.values(PriorityEnum).map((priority) => (
                    <MenuItem key={priority} value={priority}>
                      {priority}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {!effectivePartialRaid && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={readOnly}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={handleSelectChange('status') as any}
                      readOnly={readOnly}
                      inputProps={{ readOnly }}
                    >
                      {Object.values(ActionStatusEnum).map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                            
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                    Task Details
                  </Typography>
                  <Divider />
                </Grid>
              </>
            )}
            
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => option.display_name}
                value={findUserById(formData.person_responsible || '')}
                onChange={(event, newValue) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    person_responsible: newValue ? newValue.id.toString() : '' 
                  }));
                }}
                disabled={readOnly}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Person Responsible"
                    required
                    error={!!errors.person_responsible}
                    helperText={errors.person_responsible || 'Person responsible for completing this action'}
                    InputProps={{ ...params.InputProps, readOnly }}
                    disabled={readOnly}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No users found"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <InlineDatePicker
                label="Planned Completion Date"
                value={plannedCompletionDate}
                onChange={(e) => setPlannedCompletionDate(e.target.value)}
                disabled={readOnly}
                fullWidth
              />
            </Grid>

            {!effectivePartialRaid && (
              <Grid item xs={12} sm={6}>
                <InlineDatePicker
                  label="Actual Completion Date"
                  value={actualCompletionDate}
                  onChange={(e) => setActualCompletionDate(e.target.value)}
                  disabled={readOnly}
                  fullWidth
                />
              </Grid>
            )}
          </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {itemId ? 'Update' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ActionDialog;
